/**
 * ThinkingSteps — collapsible AI reasoning/tool-call steps
 *
 * During streaming: expanded, shows live thinking activity
 * After streaming: collapsed, click to expand
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Brain, Wrench, Check, X, Loader2 } from 'lucide-react';
import type { ThinkingStep } from '@/hooks/useChatStream';

interface ThinkingStepsProps {
  steps: ThinkingStep[];
  streaming?: boolean;
}

/** Friendly tool name mapping */
function friendlyToolName(name: string): string {
  const map: Record<string, string> = {
    listTenantDataSourcesTool: 'Checking tenant data sources',
    routeTenantDataQueryTool: 'Planning database route',
    listLegacyBusinessPacksTool: 'Listing legacy business packs',
    detectLegacyBusinessPackTool: 'Matching legacy business pack',
    queryLegacyBusinessPackTool: 'Running legacy business pack',
    detectReconciliationPackTool: 'Matching reconciliation pack',
    queryCrossSourceReconciliationPackTool: 'Running reconciliation pack',
    listTenantBusinessViewsTool: 'Listing business views',
    detectTenantBusinessViewTool: 'Matching business view',
    queryTenantBusinessViewTool: 'Querying business view',
    legacyDbQueryTool: 'Querying database',
    legacyDbListTablesTool: 'Listing tables',
    legacyDbDescribeTableTool: 'Checking schema',
    queryCtmJobsTool: 'Looking up trucking jobs',
    searchKnowledgeTool: 'Searching knowledge base',
    readMemoryTool: 'Reading memory',
    createJobTool: 'Creating job',
    calculateBillingTool: 'Calculating billing',
    cmsPro2QueryTool: 'Querying PRO2 system',
    cmsCfsQueryTool: 'Querying CFS/warehouse',
  };
  if (map[name]) return map[name];
  if (/preflight|readiness|package/i.test(name)) return 'Checking package readiness';
  if (/simulate|simulation/i.test(name)) return 'Reviewing simulation evidence';
  if (/onboarding|lifecycle|delivery|assignment/i.test(name)) return 'Reviewing onboarding lifecycle';
  if (/billing|credit|ledger|settlement|usage/i.test(name)) return 'Reviewing billing and credits';
  return 'Using workspace tool';
}

function sanitizeThinkingText(text: string): string {
  const replacements: Array<[RegExp, string]> = [
    [/\bcontainer trucking customer service\b/gi, 'workspace assistant'],
    [/\bcustomer service\b/gi, 'workspace assistant'],
    [/\bfinance\b/gi, 'finance workspace'],
    [/\boperations\b/gi, 'operations workspace'],
    [/\brouter\b/gi, 'workspace routing'],
    [/\bspecialist\b/gi, 'workspace step'],
    [/\bhierarchy\b/gi, 'routing'],
    [/\blane\b/gi, 'path'],
    [/\bagent\b/gi, 'assistant'],
    [/\btool[_ ]?call\b/gi, 'workspace step'],
    [/\btool[_ ]?result\b/gi, 'workspace result'],
  ];

  let sanitized = text;
  for (const [pattern, replacement] of replacements) {
    sanitized = sanitized.replace(pattern, replacement);
  }

  return sanitized
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();
}

function StepIcon({ step, streaming }: { step: ThinkingStep; streaming?: boolean }) {
  if (step.type === 'thinking') {
    return streaming
      ? <Loader2 className="h-3 w-3 animate-spin text-purple-500 shrink-0" />
      : <Brain className="h-3 w-3 text-purple-500 shrink-0" />;
  }
  if (step.type === 'tool_call') {
    return <Wrench className="h-3 w-3 text-blue-500 animate-pulse shrink-0" />;
  }
  if (step.type === 'tool_result') {
    return step.isError
      ? <X className="h-3 w-3 text-red-500 shrink-0" />
      : <Check className="h-3 w-3 text-green-500 shrink-0" />;
  }
  return null;
}

export function ThinkingSteps({ steps, streaming }: ThinkingStepsProps) {
  const [expanded, setExpanded] = useState(true);

  // Auto-collapse when streaming ends
  useEffect(() => {
    if (!streaming) {
      const timer = setTimeout(() => setExpanded(false), 600);
      return () => clearTimeout(timer);
    } else {
      setExpanded(true);
    }
  }, [streaming]);

  if (!steps.length) return null;

  // Summarize for collapsed view
  const toolCalls = steps.filter(s => s.type === 'tool_call');
  const hasThinking = steps.some(s => s.type === 'thinking');
  const summaryParts: string[] = [];
  if (hasThinking) summaryParts.push('Reviewed request');
  if (toolCalls.length > 0) summaryParts.push(`Ran ${toolCalls.length} step${toolCalls.length > 1 ? 's' : ''}`);
  const summary = summaryParts.join(' · ') || 'Prepared response';

  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors',
          'rounded-lg px-2 py-1 hover:bg-muted/50',
        )}
      >
        {streaming ? (
          <Loader2 className="h-3 w-3 animate-spin text-purple-500" />
        ) : (
          <Brain className="h-3 w-3 text-purple-400" />
        )}
        <span>{streaming ? 'Thinking…' : summary}</span>
        <ChevronDown className={cn(
          'h-3 w-3 transition-transform duration-200',
          expanded ? 'rotate-0' : '-rotate-90',
        )} />
      </button>

      <div className={cn(
        'overflow-hidden transition-all duration-300 ease-in-out',
        expanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0',
      )}>
        <div className="ml-2 mt-1 border-l-2 border-muted pl-3 space-y-1">
          {steps.map((step, i) => {
            const isLast = i === steps.length - 1;
            return (
              <div
                key={`${step.type}-${i}`}
                className={cn(
                  'flex items-start gap-1.5 text-[11px] leading-relaxed',
                  step.isError ? 'text-red-500' : 'text-muted-foreground',
                )}
              >
                <StepIcon step={step} streaming={streaming && isLast} />
                <span className="min-w-0">
                  {step.type === 'thinking' ? (
                    <span className="italic line-clamp-2">
                      {(() => {
                        const sanitized = sanitizeThinkingText(step.text);
                        const truncated = sanitized.length > 220 ? `${sanitized.slice(-220)}…` : sanitized;
                        return truncated || (streaming && isLast ? 'Reviewing your request and current workspace' : 'Request reviewed');
                      })()}
                    </span>
                  ) : step.type === 'tool_call' ? (
                    <span>{friendlyToolName(step.toolName ?? 'tool')}…</span>
                  ) : (
                    <span>
                      {friendlyToolName(step.toolName ?? 'tool')}
                      {step.isError ? ' — failed' : ' — done'}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
